import { ChevronRight } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export const Card = ({ children, className }: { children: ReactNode; className?: string }) => (
  <section className={cn('rounded-xl border border-border bg-surface', className)}>{children}</section>
);

export const CardHeader = ({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) => (
  <header className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
    <div>
      <h2 className="text-[14px] font-semibold text-ink">{title}</h2>
      {description ? <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p> : null}
    </div>
    {action}
  </header>
);

export const CardBody = ({
  children,
  className,
  id,
  hidden,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  hidden?: boolean;
}) => (
  <div id={id} hidden={hidden} className={cn('px-4 py-3', className)}>
    {children}
  </div>
);

export interface CollapsibleCardProps {
  title: ReactNode;
  children: ReactNode;
  /** Collapsed unless the caller says otherwise, and unless the reader said so before. */
  defaultOpen?: boolean;
  /**
   * Remembers folded or unfolded under this name, per browser. Without it the
   * card opens the way the page defines it on every visit.
   */
  rememberAs?: string;
  className?: string;
}

/** Where a remembered card keeps its state; one entry per `rememberAs`. */
export const collapsibleStorageKey = (name: string): string => `tms.card.${name}`;

const readRemembered = (name: string | undefined, fallback: boolean): boolean => {
  if (!name) return fallback;
  try {
    const stored = window.localStorage.getItem(collapsibleStorageKey(name));
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
    return fallback;
  } catch {
    // A browser that refuses storage still gets a working card.
    return fallback;
  }
};

/**
 * A card whose body folds away behind its own title.
 *
 * The body stays in the document and is hidden, so the toggle can point at it
 * (`aria-controls`) and the browser's find-on-page keeps working the moment it
 * is opened again. With `rememberAs`, folding is a decision the reader makes
 * once: the card comes back the way they left it, like the theme or the chosen
 * columns of a list.
 */
export const CollapsibleCard = ({
  title,
  children,
  defaultOpen = false,
  rememberAs,
  className,
}: CollapsibleCardProps) => {
  const [open, setOpen] = useState(() => readRemembered(rememberAs, defaultOpen));
  const bodyId = useId();

  useEffect(() => {
    if (!rememberAs) return;
    try {
      window.localStorage.setItem(collapsibleStorageKey(rememberAs), open ? 'open' : 'closed');
    } catch {
      // Nothing to do: the card still works for this visit.
    }
  }, [rememberAs, open]);

  return (
    <Card className={className}>
      <h2 className={cn('flex', open ? 'border-b border-border' : undefined)}>
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex w-full items-center gap-2 px-4 py-3 text-left text-[14px] font-semibold text-ink"
        >
          <ChevronRight
            className={cn(
              'size-4 shrink-0 text-ink-subtle transition-transform',
              open ? 'rotate-90' : undefined,
            )}
            aria-hidden="true"
          />
          {title}
        </button>
      </h2>
      <CardBody id={bodyId} hidden={!open}>
        {children}
      </CardBody>
    </Card>
  );
};
